import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { DASHBOARD_TABS } from '../constants';
import {
  AGENT,
  RANGE_DATE,
} from '@/components/FilterBar/constants/filtersConstants';
import { MIN_AGENTS_TO_SHOW_DROPDOWN } from '@/components/FilterBar/dropdowns/AgentsDropdown';
import { MIN_BRANCHES_TO_SHOW_DROPDOWN } from '@/components/FilterBar/dropdowns/BranchesDropdown';
import { MIN_TEAMS_TO_SHOW_DROPDOWN } from '@/components/FilterBar/dropdowns/TeamsDropdown';
import { setSelectedIds } from '@/store/slices/components/filterBar/FilterBarSlice';

/**
 * Derives DashboardFilters' visibility rules (who can see filters, which
 * ones apply, when to hide "Filtering by") and removes the dashboard's
 * preselected agent when its dropdown has no choices to offer.
 */
export default function useDashboardFiltersState(isHidden) {
  const dispatch = useDispatch();
  const { edition, tabActive } = useSelector((state) => state.DashboardSlice);
  const {
    selectedIds,
    selectAgents,
    selectBranches,
    selectTeams,
    loadingAgents,
    loadingBranches,
    loadingTeams,
    errorAgents,
    errorBranches,
    errorTeams,
  } = useSelector((state) => state.FilterBarSlice.filters);
  const { allPermissions } = useSelector(
    (state) => state.AuthSlice.permissions
  );

  const canSeeFilters = allPermissions.includes('is_company_admin');

  const showTeamsfilter = canSeeFilters;

  // The date range only applies to Performance; every other filter is
  // admin-only, so a preselected Agent/Branch/Team tag (e.g. the dashboard's
  // default agent) has to be dropped for non-admins.
  const filteredSelectedIds = selectedIds.filter((el) =>
    el.filter_type === RANGE_DATE
      ? tabActive === DASHBOARD_TABS.PERFORMANCE
      : canSeeFilters
  );

  // Agent/Branch/Team dropdowns are never mounted for non-admins, so their
  // catalogs never fetch and loadingAgents/loadingBranches/loadingTeams stay
  // stuck at their initial `true` - only count them while canSeeFilters.
  const isSomethingLoading =
    canSeeFilters &&
    (loadingAgents || loadingBranches || (showTeamsfilter && loadingTeams));
  const hasCatalogError =
    canSeeFilters &&
    (errorAgents || errorBranches || (showTeamsfilter && errorTeams));

  const hasEnoughAgents = selectAgents.length >= MIN_AGENTS_TO_SHOW_DROPDOWN;
  const hasEnoughBranches =
    selectBranches.length >= MIN_BRANCHES_TO_SHOW_DROPDOWN;
  const hasEnoughTeams =
    showTeamsfilter && selectTeams.length >= MIN_TEAMS_TO_SHOW_DROPDOWN;

  useEffect(() => {
    if (!canSeeFilters || loadingAgents || errorAgents || hasEnoughAgents)
      return;
    if (!selectedIds.some((item) => item.filter_type === AGENT)) return;

    dispatch(setSelectedIds({ idList: [], type: AGENT }));
  }, [
    canSeeFilters,
    dispatch,
    errorAgents,
    hasEnoughAgents,
    loadingAgents,
    selectedIds,
  ]);

  const shouldHideFilteringBy =
    !isSomethingLoading &&
    !hasCatalogError &&
    !hasEnoughAgents &&
    !hasEnoughBranches &&
    !hasEnoughTeams &&
    tabActive !== DASHBOARD_TABS.PERFORMANCE;

  const showSelectionTags =
    !shouldHideFilteringBy && filteredSelectedIds.length > 0 && !isHidden;

  const showFilterIcon =
    !shouldHideFilteringBy &&
    !isSomethingLoading &&
    isHidden &&
    filteredSelectedIds.length > 0;

  return {
    edition,
    tabActive,
    canSeeFilters,
    showTeamsfilter,
    filteredSelectedIds,
    shouldHideFilteringBy,
    showSelectionTags,
    showFilterIcon,
  };
}
